import { useState, useMemo, useEffect, useCallback } from 'react'
import { Search, Filter, Download, Loader2 } from 'lucide-react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, GridSizeChangedEvent } from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import { useManagerSubmissions } from '@/hooks/useManagerSubmissions'
import {
  useApproveSubmission,
  useSendBackSubmission,
  useUnapproveSubmission,
} from '@/hooks/useManagerActions'
import { useExportManagerSubmissions } from '@/hooks/useExportManagerSubmissions'
import { useDebounce } from '@/hooks/useDebounce'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Pagination } from '@/components/common/Pagination'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { ApprovalsStatsCards } from '@/components/Approvals/ApprovalsStatsCards'
import {
  SubmissionStatusBadge,
  TextCell,
  SubmittedByCell,
  DateCell,
  ApprovalActionsCell,
} from '@/components/Approvals/cellRenderers'
import type { ManagerSubmissionSummary } from '@/api/managerSubmissions'

ModuleRegistry.registerModules([AllCommunityModule])

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'SENT_BACK', label: 'Followed-Up' },
  { value: 'UNAPPROVED', label: 'Unapproved' },
]

export default function Approvals() {
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Dialogs
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [sendBackDialogOpen, setSendBackDialogOpen] = useState(false)
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false)
  const [unapproveNotes, setUnapproveNotes] = useState('')
  const [selectedSubmission, setSelectedSubmission] =
    useState<ManagerSubmissionSummary | null>(null)

  // Mutations
  const approveMutation = useApproveSubmission()
  const sendBackMutation = useSendBackSubmission()
  const unapproveMutation = useUnapproveSubmission()

  // Export
  const { exportSubmissions, isProcessing: isExporting } =
    useExportManagerSubmissions()

  const debouncedSearch = useDebounce(searchInput, 300)

  // Reset page on filter change
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, statusFilter, pageSize])

  const filters = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      page,
      pageSize,
    }),
    [debouncedSearch, statusFilter, page, pageSize]
  )

  const { data, isLoading } = useManagerSubmissions(filters)

  const submissions = (data?.data ?? []).filter(
    s => s.submission_status !== 'WITHDRAWN'
  )
  const totalPages = data?.pagination
    ? Math.ceil(data.pagination.total / pageSize)
    : 1

  // Action handlers
  const handleApproveClick = useCallback(
    (submission: ManagerSubmissionSummary) => {
      setSelectedSubmission(submission)
      setApproveDialogOpen(true)
    },
    []
  )

  const handleSendBackClick = useCallback(
    (submission: ManagerSubmissionSummary) => {
      setSelectedSubmission(submission)
      setSendBackDialogOpen(true)
    },
    []
  )

  const handleConfirmApprove = useCallback(() => {
    if (selectedSubmission) {
      approveMutation.mutate(selectedSubmission.submission_id, {
        onSettled: () => {
          setApproveDialogOpen(false)
          setSelectedSubmission(null)
        },
      })
    }
  }, [selectedSubmission, approveMutation])

  const handleConfirmSendBack = useCallback(() => {
    if (selectedSubmission) {
      sendBackMutation.mutate(selectedSubmission.submission_id, {
        onSettled: () => {
          setSendBackDialogOpen(false)
          setSelectedSubmission(null)
        },
      })
    }
  }, [selectedSubmission, sendBackMutation])

  const handleUnapproveClick = useCallback(
    (submission: ManagerSubmissionSummary) => {
      setSelectedSubmission(submission)
      setUnapproveDialogOpen(true)
    },
    []
  )

  const handleConfirmUnapprove = useCallback(() => {
    if (selectedSubmission) {
      unapproveMutation.mutate(
        {
          submissionId: selectedSubmission.submission_id,
          body: unapproveNotes.trim()
            ? { decision_notes: unapproveNotes.trim() }
            : undefined,
        },
        {
          onSettled: () => {
            setUnapproveDialogOpen(false)
            setSelectedSubmission(null)
            setUnapproveNotes('')
          },
        }
      )
    }
  }, [selectedSubmission, unapproveMutation, unapproveNotes])

  const handleExport = useCallback(() => {
    exportSubmissions({
      status: statusFilter !== 'all' ? [statusFilter] : undefined,
    })
  }, [exportSubmissions, statusFilter])

  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  const columnDefs: ColDef<ManagerSubmissionSummary>[] = useMemo(
    () => [
      {
        field: 'submission_id',
        headerName: 'ID',
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: TextCell,
        valueFormatter: params => {
          const id = params.value as string
          return id ? id.slice(0, 8) + '...' : '-'
        },
      },
      {
        field: 'plan_name',
        headerName: 'Assortment Plan',
        flex: 2,
        minWidth: 180,
        cellRenderer: TextCell,
      },
      {
        field: 'submitted_by',
        headerName: 'Requested By',
        flex: 1.5,
        minWidth: 140,
        cellRenderer: SubmittedByCell,
      },
      {
        field: 'submitted_at',
        headerName: 'Request Date',
        flex: 1,
        minWidth: 120,
        cellRenderer: DateCell,
      },
      {
        field: 'submission_status',
        headerName: 'Status',
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: SubmissionStatusBadge,
      },
      {
        headerName: 'Actions',
        flex: 1,
        minWidth: 120,
        maxWidth: 150,
        cellClass: 'ag-right-aligned-cell',
        headerClass: 'ag-right-aligned-header',
        cellRenderer: ApprovalActionsCell,
        cellRendererParams: {
          onApprove: handleApproveClick,
          onSendBack: handleSendBackClick,
          onUnapprove: handleUnapproveClick,
        },
      },
    ],
    [handleApproveClick, handleSendBackClick, handleUnapproveClick]
  )

  return (
    <>
      {/* Approve Dialog */}
      <ConfirmationDialog
        open={approveDialogOpen}
        onOpenChange={setApproveDialogOpen}
        title="Approve submission"
        description={`Are you sure you want to approve "${selectedSubmission?.plan_name ?? ''}"?`}
        confirmText="Approve"
        cancelText="Cancel"
        onConfirm={handleConfirmApprove}
        isLoading={approveMutation.isPending}
      />

      {/* Send Back Dialog */}
      <ConfirmationDialog
        open={sendBackDialogOpen}
        onOpenChange={setSendBackDialogOpen}
        title="Send back submission"
        description={`Are you sure you want to send back "${selectedSubmission?.plan_name ?? ''}" to the planner?`}
        confirmText="Send Back"
        cancelText="Cancel"
        onConfirm={handleConfirmSendBack}
        variant="destructive"
        isLoading={sendBackMutation.isPending}
      />

      {/* Unapprove Dialog */}
      <ConfirmationDialog
        open={unapproveDialogOpen}
        onOpenChange={open => {
          setUnapproveDialogOpen(open)
          if (!open) setUnapproveNotes('')
        }}
        title="Unapprove submission"
        description={`Are you sure you want to unapprove "${selectedSubmission?.plan_name ?? ''}"? The plan will return to Draft status.`}
        confirmText="Unapprove"
        cancelText="Cancel"
        onConfirm={handleConfirmUnapprove}
        variant="destructive"
        isLoading={unapproveMutation.isPending}
      >
        <div className="space-y-2 py-2">
          <label className="text-sm font-medium">Notes (optional)</label>
          <Textarea
            placeholder="Add a reason for unapproving..."
            value={unapproveNotes}
            onChange={e => setUnapproveNotes(e.target.value)}
            className="min-h-[80px] resize-none"
            maxLength={2000}
          />
        </div>
      </ConfirmationDialog>

      <div className="flex flex-col gap-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Approvals</h1>
          <p className="text-base text-foreground mt-1">
            Review and manage assortment plan submissions
          </p>
        </div>

        {/* Stats Cards */}
        <ApprovalsStatsCards />

        {/* Filter Bar */}
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
          <div className="relative flex-1 max-w-[352px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search submissions..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="pl-9 bg-muted/50"
            />
          </div>

          <Filter className="h-6 w-6 text-muted-foreground" />

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Separator orientation="vertical" className="h-10" />

          <Button
            variant="outline"
            className="gap-2"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export
          </Button>
        </div>

        {/* Table */}
        <div className="w-full">
          <div className="border border-[#e7e7e7] bg-white overflow-hidden">
            <div className="ag-theme-michaels w-full">
              <AgGridReact<ManagerSubmissionSummary>
                rowData={submissions}
                columnDefs={columnDefs}
                theme="legacy"
                loading={isLoading}
                domLayout="autoHeight"
                rowHeight={68}
                headerHeight={48}
                autoSizeStrategy={{ type: 'fitGridWidth' }}
                onGridSizeChanged={onGridSizeChanged}
                overlayLoadingTemplate="<span>Loading...</span>"
                overlayNoRowsTemplate="<span>No submissions found</span>"
                suppressCellFocus={true}
                suppressRowHoverHighlight={false}
                suppressColumnVirtualisation={true}
                suppressMovableColumns={true}
              />
            </div>

            <Pagination
              page={page}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        </div>
      </div>
    </>
  )
}
